<template>
  <div class="interview-header">
    <ProgressBar
      :value="progress"
      class="interview-header__progress"
      data-test-id="InterviewProgressBar"
    />
    <div
      class="interview-header__page-name"
      :class="{
        'interview-header__page-name--empty': !Boolean(brandImage),
      }"
    >
      <img v-if="brandImage" :src="brandImage" class="interview-header__page-name__brand-image" />
    </div>
    <div
      v-if="mediaType || imageUrl || titleHtml !== undefined"
      class="interview-header__title-container"
      :class="{
        'interview-header__title-container--align-left': !!imageUrl,
      }"
    >
      <button
        v-if="['audio', 'video'].includes(mediaType)"
        class="interview-header__media-button"
        data-test-id="InterviewHeader_VideoOrAudioButton"
        @click="$emit('viewMedia')"
      >
        <img class="interview-header__media-icon" :src="mediaIcon" />
        <span class="sr-only">Open media preview</span>
      </button>
      <button
        v-if="mediaType === 'image' && imageUrl"
        class="interview-header__image-button"
        data-test-id="InterviewHeader_ImageButton"
        @click="$emit('viewMedia')"
      >
        <img class="interview-header__image-button-image" :src="imageUrl" />
      </button>
      <div class="interview-header__text">
        <!-- eslint-disable vue/no-v-html -->
        <h1
          class="interview-header__title"
          :class="{
            'interview-header__title--narrow': ['audio', 'video'].includes(mediaType),
          }"
          v-html="titleHtml"
        />
        <!-- eslint-enable vue/no-v-html -->
        <p v-if="price" class="interview-header__price" data-test-id="InterviewHeader_Price">
          {{ price }}
        </p>
      </div>
    </div>
    <h2
      v-if="subTitle.length > 0"
      class="interview-header__subtitle"
      data-test-id="InterviewHeader_Subtitle"
    >
      {{ subTitle }}
    </h2>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue'

import { parseUserMarkdown } from '../../../interview/utils/parseUserMarkdown'
import playButtonIcon from '../../../question/assets/play-button.svg'
import type { MediaType } from '../../../question/models/Media'
import ProgressBar from '../../../shared/components/dumb/ProgressBar.vue'
import expandWhiteIcon from '../../assets/expand-white.svg'
import videoClipIcon from '../../assets/video-clip.svg'

export default defineComponent({
  components: {
    ProgressBar,
  },
  props: {
    titleMarkdown: {
      type: String,
      default: undefined,
    },
    subTitle: {
      type: String,
      default: '',
    },
    price: {
      type: String,
      default: '',
    },
    imageUrl: {
      type: String,
      default: '',
    },
    progress: {
      type: Number,
      default: 0,
    },
    mediaType: {
      type: String as PropType<MediaType | ''>,
      default: '',
    },
    brandImage: {
      type: [String, null],
      default: null,
    },
  },
  emits: {
    viewMedia: () => true,
  },
  setup(props) {
    const titleHtml = computed(() =>
      props.titleMarkdown === undefined ? undefined : parseUserMarkdown(props.titleMarkdown),
    )

    const mediaIcon = computed(() => {
      if (!props.mediaType) {
        return ''
      }
      const iconMap: Record<MediaType, string> = {
        video: videoClipIcon,
        mux: videoClipIcon,
        audio: playButtonIcon,
        image: expandWhiteIcon,
      }
      return iconMap[props.mediaType] ?? ''
    })

    return {
      titleHtml,
      mediaIcon,
    }
  },
})
</script>

<style lang="postcss">
.interview-header {
  &__media-button {
    display: flex;
    align-items: center;
    align-self: flex-end;
    justify-content: center;
    padding: 2px;
    border: 1px solid var(--color-blue-grey);
    border-radius: var(--attest-border-radius);
    background-color: transparent;
    cursor: pointer;
  }

  &__media-icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  &__page-name,
  &__title-container {
    border-bottom: 1px solid var(--color-blue-grey);
    text-align: center;
  }

  &__page-name {
    display: grid;
    align-items: center;
    justify-content: center;
    padding: 0 32px;
    grid-template-rows: minmax(0, 60px);

    &--empty {
      padding: 1.5rem;
      grid-template-rows: 0;
    }

    &__brand-image {
      max-width: 220px;
      max-height: 44px;
      object-fit: contain;
    }
  }

  &__title-container {
    display: flex;
    min-height: 84px;
    align-items: center;
    padding: 1rem;

    @media (width >= 640px) {
      min-height: 120px;
    }

    &--align-left {
      text-align: left;
    }
  }

  &__text {
    display: flex;

    /* Allows a long title to wrap rather than overflow and be clipped by the
       interview layout's hidden overflow. */
    min-width: 0;
    flex: 1;
    flex-direction: column;
  }

  &__subtitle {
    padding: 1em;
    background-color: var(--color-selected);
    color: white;
    font-size: var(--attest-font-size-m);
    text-align: center;
  }

  &__image-button {
    position: relative;
    height: 56px;
    padding: 0;
    border: 0;

    /* Wider than the visual gutter on purpose: the expand badge overhangs the
       bottom-right corner by 12px and would otherwise reach the title. */
    margin-right: var(--attest-spacing-7);
    background-color: transparent;
    cursor: pointer;
  }

  &__image-button::after {
    position: absolute;
    right: -12px;
    bottom: -12px;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background-color: var(--color-selected);
    background-image: url('../../assets/expand-white.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: auto 11px;
    color: white;
    content: ' ';
  }

  &__image-button-image {
    width: 56px;
    height: 56px;
    object-fit: contain;
  }

  &__title {
    width: 100%;
    color: #000039;
    font-size: var(--attest-font-size-m);

    &--narrow {
      width: 90%;
      padding: 0 var(--attest-spacing-4);
    }
  }

  &__price {
    width: 100%;
    margin-top: var(--attest-spacing-2);
    color: #000039;
    font-size: var(--attest-font-size-l);
    font-weight: bold;
  }

  &__progress {
    height: 0.5rem;
    border-radius: 0;
    background-color: white;
  }
}
</style>
